import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { cx } from './cx.js';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => !el.hasAttribute('inert') && el.getAttribute('aria-hidden') !== 'true',
  );
}

export interface DialogProps {
  open: boolean;
  /** Escape, the close button and a click outside call this (unless `dismissible` is false). */
  onClose(): void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Actions at the bottom (kept visible while the body scrolls). */
  footer?: ReactNode;
  /** `drawer`: a side sheet (full screen on phones). Default `modal`. */
  variant?: 'modal' | 'drawer';
  size?: 'sm' | 'md' | 'lg';
  /** Element to focus on open (default: the first field or button in the body). */
  initialFocus?: RefObject<HTMLElement | null>;
  /** False while a save runs: Escape and outside clicks do nothing. */
  dismissible?: boolean;
  className?: string;
}

/**
 * Modal dialog or side drawer: rendered over the page, focus kept inside (Tab
 * cycles), Escape closes, focus returns to what opened it, the page behind does not
 * scroll. Labelled by its title, described by its description.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  variant = 'modal',
  size = 'md',
  initialFocus,
  dismissible = true,
  className,
}: DialogProps) {
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissibleRef = useRef(dismissible);
  dismissibleRef.current = dismissible;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const root = panel.current;
    const target =
      initialFocus?.current ??
      (root
        ? (root.querySelector<HTMLElement>(
            '.qk-dialog__body input, .qk-dialog__body select, .qk-dialog__body textarea',
          ) ??
          focusables(root).find((el) => !el.classList.contains('qk-dialog__close')) ??
          root)
        : null);
    target?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      if (opener && document.contains(opener)) opener.focus();
    };
    // Only on open/close: re-renders must not move focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (dismissibleRef.current) onCloseRef.current();
      return;
    }
    if (e.key !== 'Tab' || !panel.current) return;
    const items = focusables(panel.current);
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first?.focus();
    }
  };

  return createPortal(
    <div
      className={cx('qk-dialog-layer', `qk-dialog-layer--${variant}`)}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && dismissibleRef.current) onCloseRef.current();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={description ? `${id}-description` : undefined}
        tabIndex={-1}
        className={cx('qk-dialog', `qk-dialog--${variant}`, `qk-dialog--${size}`, className)}
        onKeyDown={onKeyDown}
      >
        <header className="qk-dialog__header">
          <div className="qk-dialog__heading">
            <h2 id={`${id}-title`} className="qk-dialog__title">
              {title}
            </h2>
            {description ? (
              <p id={`${id}-description`} className="qk-dialog__description">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="qk-dialog__close"
            aria-label="Close"
            onClick={() => dismissibleRef.current && onCloseRef.current()}
          >
            ×
          </button>
        </header>
        {children ? <div className="qk-dialog__body">{children}</div> : null}
        {footer ? <footer className="qk-dialog__footer">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** `danger` for archive, block and other things that are hard to undo. */
  tone?: 'primary' | 'danger';
  /** Shows a spinner on the confirm button and blocks dismissing. */
  busy?: boolean;
  onConfirm(): void;
  onCancel(): void;
}

/** "Are you sure?" with the action named on the button. Cancel has the focus first. */
export function ConfirmDialog({
  open,
  title,
  description,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'primary',
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const cancel = useRef<HTMLButtonElement>(null);
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      description={description}
      size="sm"
      initialFocus={cancel}
      dismissible={!busy}
      footer={
        <>
          <button
            ref={cancel}
            type="button"
            className="qk-btn qk-btn--secondary qk-btn--md"
            onClick={onCancel}
            disabled={busy}
          >
            <span className="qk-btn__label">{cancelLabel}</span>
          </button>
          <button
            type="button"
            className={cx('qk-btn', `qk-btn--${tone}`, 'qk-btn--md')}
            onClick={onConfirm}
            disabled={busy}
            aria-busy={busy || undefined}
          >
            <span className="qk-btn__label">{busy ? 'Working…' : confirmLabel}</span>
          </button>
        </>
      }
    >
      {children}
    </Dialog>
  );
}
